import React from "react";
import {
  Box,
  Typography,
  FormControl,
  FormHelperText,
  useTheme,
  useMediaQuery,
  Autocomplete,
  TextField,
} from "@mui/material";
import { RequireIcon as CustomRequireIcon } from "@ippon/shared-assets";
import "./CustomAutocomplete.css";
import { Controller } from "react-hook-form";
import { useResponsiveMenuPlacement } from "../../../hooks/useResponsiveMenuPlacement";

export interface CustomAutocompleteProps {
  label: string;
  name: string;
  placeholder?: string;
  control?: any;
  errors?: any;
  disabled?: boolean;
  readonly?: boolean;
  isRequired?: boolean;
  extraClassName?: string;
  isShowInColumn?: boolean;
  options?: Array<{ value: string | number; label: string }>;
  labelWidth?: string;
  labelWidthSp?: string;
  inputWidth?: string;
  inputWidthSp?: string;
  onChangeValue?: (val: any) => void;
  freeSolo?: boolean;
  hasSpacing?: boolean;
}

const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  label,
  name,
  placeholder = "",
  control,
  errors = {},
  disabled = false,
  readonly = false,
  isRequired = false,
  extraClassName = "flexRow",
  isShowInColumn = false,
  options = [],
  labelWidth = "98px",
  labelWidthSp = "100px",
  inputWidth = "100%",
  inputWidthSp = "100%",
  onChangeValue,
  freeSolo = true,
  hasSpacing,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { menuMaxHeight, popperPlacement, updateMenuPlacement } =
    useResponsiveMenuPlacement(360);

  const formattedOptions = options.map((option) => ({
    label: option.label || String(option.value),
    value: String(option.value),
  }));

  return (
    <Box
      className={`inputGroupWrapper ${
        isShowInColumn ? "isShowInColumn" : ""
      } ${extraClassName} ${isMobile ? "sp" : ""} ${
        hasSpacing === false ? "noSpacing" : ""
      }`}
      // AI 画面コンテキスト用 (getPageContext が拾う)。
      // 呼び出し側はラッパー不要で name/label/options が自動的に AI に伝わる。
      data-field={name || undefined}
      data-label={label || undefined}
      data-options={
        options && options.length > 0 ? JSON.stringify(options) : undefined
      }
      sx={{
        display: "flex !important",
        width: { xs: "100%", sm: "inherit" },
      }}
    >
      <Typography
        className="inputLabel"
        sx={{
          width: { sm: labelWidth, xs: labelWidthSp ?? "47%" },
          whiteSpace: "nowrap",
          fontSize: "12px !important",
          marginBottom: "1px",
        }}
        paddingBottom={
          errors[name] && extraClassName !== "flexCol"
            ? "24px"
            : extraClassName === "flexCol"
              ? "10px"
              : "10px"
        }
      >
        {label}
        {isRequired && (
          <span style={{ display: "flex", marginLeft: "4px" }}>
            <CustomRequireIcon />
          </span>
        )}
      </Typography>
      <Box
        sx={{
          width: { sm: inputWidth, xs: inputWidthSp },
        }}
      >
        <FormControl
          fullWidth
          error={isRequired ? !!errors[name] : false}
          disabled={disabled}
        >
          <Controller
            name={name}
            control={control}
            rules={isRequired ? { required: `${label}は必須です` } : {}}
            render={({ field }) => (
              <Autocomplete
                {...field}
                freeSolo={freeSolo}
                options={formattedOptions}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  return option.label || "";
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === "string") {
                    return option.value === value || option.label === value;
                  }
                  return option.value === value?.value;
                }}
                value={
                  field.value
                    ? formattedOptions.find(
                        (option) => option.value === field.value,
                      ) || field.value
                    : null
                }
                onChange={(_, newValue) => {
                  const value =
                    typeof newValue === "string"
                      ? newValue
                      : newValue?.value || "";
                  field.onChange(value);
                  if (onChangeValue) onChangeValue(value);
                }}
                onInputChange={(_, newInputValue) => {
                  if (freeSolo) {
                    field.onChange(newInputValue);
                    if (onChangeValue) onChangeValue(newInputValue);
                  }
                }}
                disabled={disabled}
                readOnly={readonly}
                onOpen={(event) =>
                  updateMenuPlacement(event.currentTarget as HTMLElement)
                }
                // popupIcon={<DropDownArrowIcon />}
                clearIcon={null}
                disableClearable={true}
                forcePopupIcon={false}
                componentsProps={{
                  popper: {
                    className: "custom-autocomplete-popup",
                    placement: popperPlacement,
                  },
                  paper: {
                    style: { maxHeight: menuMaxHeight },
                  },
                }}
                slotProps={{
                  popupIndicator: {
                    sx: {
                      display: readonly || disabled ? "none" : "block",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={placeholder}
                    className={`custom-autocomplete-input ${
                      isMobile ? "sp" : ""
                    }`}
                    sx={{
                      "& .MuiInput-underline:before": {
                        display: "none",
                      },
                      "& .MuiInput-underline:after": {
                        display: "none",
                      },
                      "& .MuiInput-underline:hover:before": {
                        display: "none",
                      },
                      "& .MuiInputBase-root": {
                        height: { sm: "34px", xs: "32px" },
                        fontSize: "12px",
                        backgroundColor: readonly ? "#F5F5F5" : "#ffffff",
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors[name] ? "#d32f2f" : "#D9D9D9",
                          borderRadius: "8px",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors[name] ? "#d32f2f" : "#D9D9D9",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors[name] ? "#d32f2f" : "#D9D9D9",
                          borderWidth: "1px",
                        },
                        "&:hover": {
                          backgroundColor: readonly ? "#F5F5F5" : "#ffffff",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#F5F5F5 !important",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#D9D9D9 !important",
                          },
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: readonly
                          ? "#3e3e3e"
                          : disabled
                            ? "#3e3e3e"
                            : "inherit",
                        fontSize: "12px",
                        padding: "0 14px",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        "&::placeholder": {
                          color: "#989898",
                          opacity: 1,
                        },
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        color: "#3e3e3e !important",
                        WebkitTextFillColor: "#3e3e3e !important",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        top: "calc(50% + 0px)",
                        transform: "translateY(-50%)",
                        right: "12px !important",
                        "& .MuiSvgIcon-root": {
                          fontSize: "14px",
                          display: readonly
                            ? "none"
                            : disabled
                              ? "none"
                              : "block",
                          color: "#666",
                        },
                      },
                    }}
                  />
                )}
              />
            )}
          />
          {isRequired && errors[name] && (
            <FormHelperText>{errors[name]?.message}</FormHelperText>
          )}
        </FormControl>
      </Box>
    </Box>
  );
};

export default CustomAutocomplete;
